import { useEffect, useMemo, useState } from 'react'
import { useLocation } from 'react-router-dom'
import {
  GatekeeperFlag,
  IsGatekeeperEnabled,
  useAgentChat,
} from 'src/context/Gatekeeper'
import { GetTrustAccountRequest } from '@trustero/trustero-api-web/lib/trust/trust_pb'
import { PublicTrustServicePromiseClient } from '@trustero/trustero-api-web/lib/trust/trust_grpc_web_pb'
import { useSwrImmutableGrpc } from 'src/components/async/useSwrImmutableGrpc'

export type PublicTrustPortalAccountResponse = {
  trustAccountId?: string
  isEnabled?: boolean
  trustPath?: string
}

export const usePublicTrustPortalAccount =
  (): PublicTrustPortalAccountResponse => {
    const isTrustPortalEnabled =
      IsGatekeeperEnabled(GatekeeperFlag.TRUST_PORTAL) &&
      process.env.NODE_ENV !== 'test'

    const [trustAccountId, setTrustAccountId] = useState<string | undefined>(
      isTrustPortalEnabled ? undefined : '',
    )

    const [isEnabled, setIsEnabled] = useState<boolean | undefined>(undefined)

    const [trustPath, setTrustPath] = useState<string | undefined>(undefined)

    const request = useMemo(() => {
      const grpcRequest = new GetTrustAccountRequest()
      grpcRequest.setDomain(window.location.host)
      const tokens = window.location.pathname.split('/')
      // we want to support
      // 1. custom domain --> trust.company.co
      // 2. app.trustero.com/<trust-portal-account-id>
      // 3. trustero.com/trust/<org-name>
      const accountId = tokens[1]
      const trustAlias =
        (tokens[1] ?? '').toLowerCase() === 'trust' ? tokens[2] : ''
      if (trustAlias) {
        grpcRequest.setTrustAlias(trustAlias)
      } else if (accountId) {
        grpcRequest.setAccountId(accountId)
      }

      if (trustAlias) {
        setTrustPath(`/trust/${trustAlias}`)
      } else if (
        accountId &&
        window.location.hostname.endsWith('trustero.com')
      ) {
        setTrustPath(`/${accountId}`)
      }

      return grpcRequest
    }, [])

    const { response } = useSwrImmutableGrpc(
      PublicTrustServicePromiseClient.prototype.getTrustAccount,
      request,
      isTrustPortalEnabled && trustAccountId === undefined,
    )

    useEffect(() => {
      if (isTrustPortalEnabled) {
        setTrustAccountId((prev) => {
          if (prev !== undefined) {
            return prev
          }
          if (response.isLoading) {
            return prev
          }
          return response?.data?.getAccountId()
        })

        setIsEnabled((prev) => {
          if (prev !== undefined) {
            return prev
          }
          if (response.isLoading) {
            return prev
          }
          return response?.data?.getEnabled()
        })
      }
    }, [response, isTrustPortalEnabled])

    return useMemo(
      () => ({
        trustAccountId,
        isEnabled,
        trustPath,
      }),
      [trustAccountId, isEnabled, trustPath],
    )
  }

export const useAiBackground = (): boolean => {
  const isAgentChat = useAgentChat()
  const location = useLocation()
  const aiBackground =
    isAgentChat && location.pathname.includes('trustero_intelligence')
  return aiBackground
}
